import { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, TrendingUp, Clock, BookOpen } from 'lucide-react';
import { api } from '../lib/api';
import BottomNav from '../components/BottomNav';

interface StudyRecord {
  date: string;
  duration: number;
  cards_count: number;
}

export default function Calendar() {
  const [records, setRecords] = useState<StudyRecord[]>([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [stats, setStats] = useState({
    totalDays: 0,
    totalMinutes: 0,
    totalCards: 0,
    streak: 0
  });

  useEffect(() => {
    loadRecords();
  }, [currentMonth]);

  const loadRecords = async () => {
    try {
      const response = await api.get('/stats/calendar', {
        params: {
          year: currentMonth.getFullYear(),
          month: currentMonth.getMonth() + 1
        }
      });
      setRecords(response.data.records || []);
      setStats(response.data.stats || stats);
    } catch (error) {
      console.error('加载学习记录失败:', error);
    }
  };

  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    // 填充空白
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    // 填充日期
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const getRecordForDate = (day: number) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return records.find(r => r.date === dateStr);
  };

  const getIntensityColor = (duration: number) => {
    if (duration === 0) return 'bg-white/5';
    if (duration < 300) return 'bg-[#22a9d9]/30';
    if (duration < 900) return 'bg-[#22a9d9]/60';
    return 'bg-[#22a9d9]';
  };

  const days = getDaysInMonth();
  const weekDays = ['日', '一', '二', '三', '四', '五', '六'];

  return (
    <div className="min-h-screen bg-[#1a2e3e] pb-24">
      {/* 头部 */}
      <div className="p-6">
        <h1 className="text-3xl font-bold text-white mb-6">学习日历</h1>

        {/* 统计卡片 */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="glass rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <CalendarIcon className="w-4 h-4 text-[#22a9d9]" />
              <span className="text-white/60 text-sm">学习天数</span>
            </div>
            <div className="text-2xl font-bold text-white">{stats.totalDays}</div>
          </div>

          <div className="glass rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-[#6c5ce7]" />
              <span className="text-white/60 text-sm">学习时长</span>
            </div>
            <div className="text-2xl font-bold text-white">
              {Math.floor(stats.totalMinutes / 60)}h
            </div>
          </div>

          <div className="glass rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="w-4 h-4 text-[#22a9d9]" />
              <span className="text-white/60 text-sm">学习卡片</span>
            </div>
            <div className="text-2xl font-bold text-white">{stats.totalCards}</div>
          </div>

          <div className="glass rounded-2xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-[#6c5ce7]" />
              <span className="text-white/60 text-sm">连续天数</span>
            </div>
            <div className="text-2xl font-bold text-white">{stats.streak}</div>
          </div>
        </div>
      </div>

      {/* 日历 */}
      <div className="px-6">
        <div className="glass rounded-2xl p-4">
          {/* 月份选择 */}
          <div className="flex items-center justify-between mb-4">
            <button
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1))}
              className="text-white/60 hover:text-white"
            >
              ←
            </button>
            <span className="text-white font-medium">
              {currentMonth.getFullYear()}年{currentMonth.getMonth() + 1}月
            </span>
            <button
              onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1))}
              className="text-white/60 hover:text-white"
            >
              →
            </button>
          </div>

          {/* 星期标题 */}
          <div className="grid grid-cols-7 gap-2 mb-2">
            {weekDays.map(day => (
              <div key={day} className="text-center text-white/40 text-sm py-2">
                {day}
              </div>
            ))}
          </div>

          {/* 日期网格 */}
          <div className="grid grid-cols-7 gap-2">
            {days.map((day, index) => {
              if (day === null) {
                return <div key={index} />;
              }

              const record = getRecordForDate(day);
              const duration = record?.duration || 0;

              return (
                <div
                  key={index}
                  className={`aspect-square rounded-lg ${getIntensityColor(duration)} flex items-center justify-center relative group`}
                >
                  <span className="text-white text-sm">{day}</span>
                  {duration > 0 && (
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block">
                      <div className="glass rounded-lg px-3 py-2 text-xs text-white whitespace-nowrap">
                        {Math.floor(duration / 60)}分钟 · {record.cards_count}张卡片
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* 图例 */}
          <div className="flex items-center justify-end gap-2 mt-4 text-xs text-white/60">
            <span>少</span>
            <div className="w-4 h-4 rounded bg-white/5" />
            <div className="w-4 h-4 rounded bg-[#22a9d9]/30" />
            <div className="w-4 h-4 rounded bg-[#22a9d9]/60" />
            <div className="w-4 h-4 rounded bg-[#22a9d9]" />
            <span>多</span>
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
}
